import Highcharts, { format } from 'highcharts';
import Grid from '@highcharts/grid-lite/es-modules/masters/grid-lite.src.js';
import { getSPP, filterByCategory } from './data.js';
import { gradientHex } from '../helpers/colors.js';

const REVENUE_COLOR = '#2222e668';
const FINES_COLOR = '#8e44ad';
const MANDATES_COLOR = '#e74c3c';

function buildSPPCityMap() {
    const byCity = {};
    getSPP().forEach(entry => {
        if (!byCity[entry.city]) byCity[entry.city] = {};
        byCity[entry.city][entry.metric] = { value: entry.value };
    });
    return byCity;
}
function fmt(value, decimals, suffix = '') {
    if (value === null || value === undefined) return '–';
    return value.toLocaleString('pl-PL', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }) + suffix;
}

/**
 * Wykres 1: Opłaty za postój i opłaty dodatkowe na mieszkańca (wykres skumulowany)
 * @param {string} id
 */
export function chartSPPRevenue(id) {
    if (!document.getElementById(id)) return;

    const sppByCity = buildSPPCityMap();
    const smData = filterByCategory(['Liczba mieszkańców']);
    const popByCity = {};
    smData.forEach(d => { popByCity[d.city] = d['Liczba mieszkańców']; });

    const revenueKey = 'Suma zebranych opłat za postój';
    const finesKey = 'Łączna kwota pobranych opłat za złamanie regulaminu parkowania';

    const rows = Object.entries(sppByCity)
        .filter(([city, metrics]) => metrics[revenueKey] !== undefined && popByCity[city])
        .map(([city, metrics]) => {
            const revenueEntry = metrics[revenueKey];
            const finesEntry = metrics[finesKey];
            const pop = popByCity[city];
            const parking = revenueEntry.value;
            const fines = finesEntry ? finesEntry.value : 0;
            return {
                city,
                parking,
                fines,
                perPersonParking: Math.round(parking / pop),
                perPersonFines: Math.round(fines / pop),
                perPersonTotal: Math.round(parking / pop) + Math.round(fines / pop)
            };
        })
        .sort((a, b) => b.perPersonTotal - a.perPersonTotal);

    const byCity = {};
    rows.forEach(d => { byCity[d.city] = d; });
    const categories = rows.map(d => d.city);

    Highcharts.chart(id, {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Wpływy z opłat za postój na mieszkańca' },
        subtitle: { text: 'Brak danych dla Zielonej Góry i Lublina.' },
        xAxis: {
            categories,
            title: { text: null },
            labels: { style: { fontSize: '14px' } }
        },
        yAxis: {
            title: { text: null },
            labels: { format: '{value} zł' },
            stackLabels: {
                enabled: true,
                formatter: function () {
                    return `${this.total.toLocaleString('pl-PL')} zł`;
                },
                style: { fontSize: '12px', fontWeight: 'bold', color: '#333' }
            }
        },
        legend: {
            enabled: true,
            verticalAlign: 'top',
            reversed: true
        },
        tooltip: {
            formatter: function () {
                const d = byCity[this.key];
                if (!d) return false;
                return `<b>${d.city}</b><br>
                    Opłaty za postój: <b>${d.perPersonParking.toLocaleString('pl-PL')} zł</b>/mieszk.<br>
                    Opłaty dodatkowe: <b>${d.perPersonFines.toLocaleString('pl-PL')} zł</b>/mieszk.<br>
                    Łącznie: <b>${d.perPersonTotal.toLocaleString('pl-PL')} zł</b>/mieszk.`;
            }
        },
        plotOptions: {
            bar: {
                stacking: 'normal',
                borderRadius: 4,
                dataLabels: { enabled: false }
            }
        },
        credits: { enabled: false },
        series: [
            {
                name: 'Opłaty dodatkowe za naruszenia regulaminu',
                color: FINES_COLOR,
                data: rows.map(d => ({
                    y: d.perPersonFines,
                    color: FINES_COLOR
                }))
            },
            {
                name: 'Opłaty za postój',
                color: REVENUE_COLOR,
                data: rows.map(d => ({
                    y: d.perPersonParking,
                    color: REVENUE_COLOR
                }))
            }
        ]
    });
}

/**
 * Wykres 2: Mandaty SM (PRD) vs opłaty dodatkowe SPP – wartości bezwzględne (stacked bar)
 * @param {string} id
 */
export function chartSPPAbsolute(id) {
    if (!document.getElementById(id)) return;

    const sppByCity = buildSPPCityMap();
    const smData = filterByCategory(['Mandaty: wartość wystawionych dot. PRD']);
    const smFinesByCity = {};
    smData.forEach(d => { smFinesByCity[d.city] = d['Mandaty: wartość wystawionych dot. PRD']; });

    const finesKey = 'Łączna kwota pobranych opłat za złamanie regulaminu parkowania';
    const revenueKey = 'Suma zebranych opłat za postój';

    const totals = {
        sppFines: 0,
        sppRevenue: 0,
        smFines: 0
    }

    const rows = Object.entries(sppByCity)
        .filter(([city, metrics]) => metrics[finesKey] !== undefined && smFinesByCity[city])
        .map(([city, metrics]) => {
            const sppEntry = metrics[finesKey];
            const sppRevenue = metrics[revenueKey];
            const smFines = smFinesByCity[city];
            totals.sppFines += sppEntry.value
            totals.sppRevenue += sppRevenue.value
            totals.smFines += smFines
            return {
                city,
                sppFines: sppEntry.value,
                sppRevenue: sppRevenue.value,
                smFines,
                total: smFines + sppEntry.value
            };
        })
        .sort((a, b) => b.total - a.total);

    const rowsWithTotals = [...rows]
    rowsWithTotals.push({
        city: "Łącznie",
        ...totals,
        total: totals.smFines + totals.sppFines
    })

    const byCity = {};
    rows.forEach(d => { byCity[d.city] = d; });

    const toMln = v => (Math.round(v / 1e5) / 10).toLocaleString('pl-PL');

    Grid.grid(id + '-grid', {
        dataTable: {
            columns: {
                "Miasto": rowsWithTotals.map(r => r.city),
                "SPP:<br>opłaty za postój": rowsWithTotals.map(r => `${toMln(r.sppRevenue)} mln.`),
                "SPP:<br>opłaty dodatkowe": rowsWithTotals.map(r => `${toMln(r.sppFines)} mln.`),
                "Mandaty SM": rowsWithTotals.map(r => `${toMln(r.smFines)} mln.`),
                "Proporcja kar": rowsWithTotals.map(r => `${fmt(r.sppFines / r.smFines, 2, '')}`)
            }
        },
        rendering: {
            rows: { strictHeights: false },
            theme: 'hcg-theme-default theme-compact'
        },
        columns: [
            { id: "Miasto", cells: { className: 'bold' } },
            { id: "SPP:<br>opłaty za postój", cells: { className: 'cell-right' } },
            { id: "SPP:<br>opłaty dodatkowe", cells: { className: 'cell-right' } },
            { id: "Mandaty SM", cells: { className: 'cell-right' } },
            { id: "Proporcja kar", cells: { className: 'cell-right' } }
        ]
    });


    Highcharts.chart(id, {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Mandaty SM (PRD) i opłaty dodatkowe SPP – łączna wartość' },
        subtitle: { text: 'Brak danych dla Zielonej Góry i Lublina.' },
        xAxis: {
            categories: rows.map(d => d.city),
            title: { text: null },
            labels: { style: { fontSize: '14px' } }
        },
        yAxis: {
            title: { text: null },
            labels: {
                formatter: function () {
                    return `${toMln(this.value)} mln zł`;
                }
            },
            stackLabels: {
                enabled: true,
                formatter: function () {
                    return `${toMln(this.total)} mln zł`;
                },
                style: { fontSize: '12px', fontWeight: 'bold', color: '#333' }
            }
        },
        legend: {
            enabled: true,
            verticalAlign: 'top',
            reversed: true
        },
        tooltip: {
            formatter: function () {
                const d = byCity[this.key];
                if (!d) return false;
                return `<b>${d.city}</b><br>
                    Mandaty SM (PRD): <b>${Math.round(d.smFines).toLocaleString('pl-PL')} zł</b><br>
                    Opłaty dodatkowe SPP: <b>${Math.round(d.sppFines).toLocaleString('pl-PL')} zł</b><br>
                    Łącznie: <b>${Math.round(d.total).toLocaleString('pl-PL')} zł</b>`;
            }
        },
        plotOptions: {
            bar: {
                stacking: 'normal',
                borderRadius: 4,
                dataLabels: { enabled: false }
            }
        },
        credits: { enabled: false },
        series: [
            {
                name: 'Opłaty dodatkowe SPP',
                data: rows.map(d => ({
                    y: d.sppFines
                })),
                color: FINES_COLOR
            },
            {
                name: 'Mandaty SM (PRD)',
                color: MANDATES_COLOR,
                data: rows.map(d => ({ y: d.smFines }))
            }
        ]
    });
}

/**
 * Wykres 3: Opłaty za złamanie regulaminu SPP jako % wartości mandatów SM
 * @param {string} id
 */
export function chartSPPFinesShare(id) {
    if (!document.getElementById(id)) return;

    const sppByCity = buildSPPCityMap();
    const smData = filterByCategory(['Mandaty: wartość wystawionych dot. PRD']);
    const smFinesByCity = {};
    smData.forEach(d => { smFinesByCity[d.city] = d['Mandaty: wartość wystawionych dot. PRD']; });

    const metricKey = 'Łączna kwota pobranych opłat za złamanie regulaminu parkowania';

    const rows = Object.entries(sppByCity)
        .filter(([city, metrics]) => metrics[metricKey] !== undefined && smFinesByCity[city])
        .map(([city, metrics]) => {
            const entry = metrics[metricKey];
            const smFines = smFinesByCity[city];
            return {
                city,
                sppFines: entry.value,
                smFines,
                ratio: Math.round(entry.value / smFines * 10) / 10
            };
        })
        .sort((a, b) => b.ratio - a.ratio);

    const byCity = {};
    rows.forEach(d => { byCity[d.city] = d; });

    Highcharts.chart(id, {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Opłaty za złamanie regulaminu SPP a mandaty SM' },
        subtitle: { text: 'Opłaty dodatkowe SPP / mandaty SM.' },
        xAxis: {
            categories: rows.map(d => d.city),
            title: { text: null },
            labels: { style: { fontSize: '14px' } }
        },
        yAxis: {
            title: { text: null },
            labels: { format: '{value}' },
            min: 0
        },
        legend: { enabled: false },
        tooltip: {
            formatter: function () {
                const d = byCity[this.key];
                if (!d) return false;
                return `<b>${d.city}</b><br>
                    Opłaty SPP za naruszenia: ${Math.round(d.sppFines).toLocaleString('pl-PL')} zł<br>
                    Mandaty SM: ${Math.round(d.smFines).toLocaleString('pl-PL')} zł<br>
                    <b>Proporcja: ${d.ratio}</b>`;
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        return `${this.y}`;
                    },
                    style: { fontSize: '12px', textOutline: 'none' }
                }
            }
        },
        credits: { enabled: false },
        series: [{
            name: 'Opłaty dodatkowe w SPP / mandaty SM',
            type: 'bar',
            yAxis: 0,
            id: 'main',
            color: '#FFFF00',
            data: rows.map(d => ({
                y: d.ratio,
                color: gradientHex('#00881e', '#FF0000', d.ratio / 2)
            }))
        }]
    });
}
